import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { 
  authApi, 
  projectsApi, 
  filesApi, 
  clausesApi, 
  reviewApi, 
  reportsApi, 
  exportsApi,
  exemptionsApi,
  userEditsApi,
  loadTokens, 
  clearTokens, 
  setTokens,
  getAccessToken,
  UserResponse,
  ProjectResponse,
  ProjectDetailResponse,
  ClauseDetailResponse,
  ReviewResultResponse,
  ReportResponse,
  ExportResponse,
  ApiError,
} from '../services/api';
import { User, Project, Clause, ReviewResult, Report, ExportJob, ReviewStatus } from '../types';

// 审查流程步骤类型
export type ReviewProcessStep = 'idle' | 'uploading' | 'splitting' | 'reviewing' | 'reporting' | 'exporting' | 'completed' | 'failed';

const compareClauses = (a: Clause, b: Clause): number => {
  return (a.sequence || 0) - (b.sequence || 0);
};

interface ReviewProcessState {
  currentStep: ReviewProcessStep;
  stepProgress: number;
  errorMessage: string | null;
}

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Current project
  currentProject: Project | null;
  
  // Data
  clauses: Clause[];
  reviewResults: Map<string, ReviewResult>;
  report: Report | null;
  currentExport: ExportJob | null;
  
  // UI state
  error: string | null;
  
  // 审查流程状态
  reviewProcess: ReviewProcessState;
}

interface AppContextType extends AppState {
  // Auth actions
  loginWithAzure: () => void;
  verifyAzureToken: (jwtToken: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  unifiedAuthEnabled: boolean;
  
  // Project actions
  createProject: (name: string, description?: string, userPrompt?: string) => Promise<Project>;
  loadProject: (projectId: string) => Promise<void>;
  updateProject: (projectId: string, data: Partial<{ name: string; description: string; user_prompt: string }>) => Promise<void>;
  clearCurrentProject: () => void;
  
  // File actions
  uploadFile: (projectId: string, file: File, fileType: 'contract' | 'standard' | 'exception') => Promise<{ id: string }>;
  
  // Clause actions
  startSplit: (projectId: string) => Promise<void>;
  loadClauses: (projectId: string) => Promise<void>;
  pollSplitStatus: (projectId: string) => Promise<boolean>;
  
  // Review actions
  startReview: (projectId: string) => Promise<void>;
  pollReviewStatus: (projectId: string) => Promise<{ completed: boolean; progress: number }>;
  loadReviewResults: (projectId: string) => Promise<void>;
  reviewSingleClause: (projectId: string, clauseId: string, force?: boolean) => Promise<ReviewResult>;
  
  // User edit actions
  saveUserEdit: (projectId: string, clauseId: string, editLines: string[], finalText?: string, notes?: string) => Promise<void>;
  
  // Report actions
  generateReport: (projectId: string) => Promise<void>;
  loadReport: (projectId: string) => Promise<void>;
  
  // Export actions
  createExport: (projectId: string, format: 'docx' | 'pdf', options?: any) => Promise<ExportJob>;
  pollExportStatus: (projectId: string, exportId: string) => Promise<ExportJob>;
  
  // Exemption actions
  addExemption: (projectId: string, data: any) => Promise<void>;
  loadExemptions: (projectId: string) => Promise<void>;
  
  // 审查流程控制
  setReviewProcess: (state: Partial<ReviewProcessState>) => void;
  resetReviewProcess: () => void;
  
  // Error handling
  clearError: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [unifiedAuthEnabled, setUnifiedAuthEnabled] = useState(
    import.meta.env.VITE_ENABLE_UNIFIED_AUTH === 'true'
  );

  const [state, setState] = useState<AppState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    currentProject: null,
    clauses: [],
    reviewResults: new Map(),
    report: null,
    currentExport: null,
    error: null,
    reviewProcess: {
      currentStep: 'idle',
      stepProgress: 0,
      errorMessage: null,
    },
  });

  // Initialize auth on mount
  useEffect(() => {
    loadTokens();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    let authEnabled = import.meta.env.VITE_ENABLE_UNIFIED_AUTH === 'true';
    let devAuthEnabled = import.meta.env.VITE_DEV_DIRECT_AUTH === 'true';
    if (!authEnabled) {
      try {
        const statusRes = await authApi.getAuthStatus();
        authEnabled = statusRes.unified_auth_enabled;
        devAuthEnabled = Boolean(statusRes.dev_auth_enabled);
        setUnifiedAuthEnabled(authEnabled);
      } catch {
        // Back-end unreachable or not configured
      }
    }

    if (!authEnabled && devAuthEnabled && !getAccessToken()) {
      try {
        const res = await authApi.verifyAzureToken('dev-local');
        if (res.success && res.user) {
          setTokens(res.access_token, res.refresh_token);
          setState(prev => ({
            ...prev,
            user: res.user as User,
            isAuthenticated: true,
            isLoading: false,
          }));
          return;
        }
      } catch {
        // Fall through to normal token checks.
      }
    }

    if (authEnabled) {
      try {
        const userRes = await authApi.getSessionUser();
        if (userRes.authenticated && userRes.user_info) {
          try {
            const user = await authApi.me();
            setState(prev => ({
              ...prev,
              user: user as User,
              isAuthenticated: true,
              isLoading: false,
            }));
            return;
          } catch {
            // session is good but /me failed (e.g. user missing entirely from DB?)
            setState(prev => ({
              ...prev,
              user: {
                id: '0',
                tpid: userRes.user_info?.id || '0',
                name: userRes.user_info?.name || '',
                username: userRes.user_info?.name || '',
                email: userRes.user_info?.email || '',
                role: 'user',
              } as any,
              isAuthenticated: true,
              isLoading: false,
            }));
            return;
          }
        }
      } catch {
        // session check failed
      }
    }

    // Default Token Check (Backward Compatibility or pure Bearer flow)
    try {
      if (!getAccessToken()) {
        setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false }));
        return;
      }
      
      const isDemoMode = localStorage.getItem('demo_mode') === 'true';
      if (isDemoMode && getAccessToken() === 'demo_access_token') {
        const user = await authApi.me().catch(() => ({
          id: '00000000-0000-0000-0000-000000000001',
          tpid: 'demo_tpid',
          email: 'demo@tpcn.com',
          name: '演示用户',
          role: 'user',
          is_active: true,
        }));
        setState(prev => ({
          ...prev,
          user: user as User,
          isAuthenticated: true,
          isLoading: false,
        }));
        return;
      }
      
      const user = await authApi.me();
      setState(prev => ({
        ...prev,
        user: user as User,
        isAuthenticated: true,
        isLoading: false,
      }));
    } catch (e) {
      clearTokens();
      localStorage.removeItem('demo_mode');
      setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false, user: null }));
    }
  };

  const loginWithAzure = async () => {
    try {
      const { login_url } = await authApi.getLoginUrl();
      window.location.href = login_url;
    } catch (e) {
      setState(prev => ({ ...prev, error: 'Failed to initiate SSO login' }));
    }
  };

  const verifyAzureToken = async (jwtToken: string) => {
    const res = await authApi.verifyAzureToken(jwtToken);
    if (res.success && res.user) {
      setTokens(res.access_token, res.refresh_token);
      setState(prev => ({ ...prev, user: res.user as User, isAuthenticated: true }));
    }
  };

  const logout = async () => {
    try {
      const res = await authApi.logout();
      clearTokens();
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        currentProject: null,
        clauses: [],
        reviewResults: new Map(),
        report: null,
      }));
      // Redirect to Azure if unified auth is enabled
      if (res.azure_logout_url && unifiedAuthEnabled) {
        window.location.href = res.azure_logout_url;
        return;
      }
    } catch (e) {
      clearTokens();
      setState(prev => ({ ...prev, user: null, isAuthenticated: false }));
    }
  };

  const createProject = async (name: string, description?: string, userPrompt?: string): Promise<Project> => {
    const project = await projectsApi.create({ name, description, user_prompt: userPrompt });
    setState(prev => ({ ...prev, currentProject: project as Project }));
    return project as Project;
  };

  const loadProject = async (projectId: string) => {
    try {
      const project = await projectsApi.get(projectId);
      setState(prev => ({ ...prev, currentProject: project as Project }));
    } catch (e) {
      setState(prev => ({ ...prev, error: 'Failed to load project' }));
    }
  };

  const updateProject = async (projectId: string, data: Partial<{ name: string; description: string; user_prompt: string }>) => {
    const project = await projectsApi.update(projectId, data);
    setState(prev => ({ ...prev, currentProject: project as Project }));
  };

  const clearCurrentProject = () => {
    setState(prev => ({
      ...prev,
      currentProject: null,
      clauses: [],
      reviewResults: new Map(),
      report: null,
      currentExport: null,
      error: null,
      reviewProcess: { currentStep: 'idle', stepProgress: 0, errorMessage: null },
    }));
  };

  const uploadFile = async (projectId: string, file: File, fileType: 'contract' | 'standard' | 'exception'): Promise<{ id: string }> => {
    const result = await filesApi.upload(projectId, file, fileType);
    // Reload project to get updated file count
    await loadProject(projectId);
    return { id: result.id };
  };

  const startSplit = async (projectId: string) => {
    await clausesApi.split(projectId, { strategy: 'auto' });
  };

  const pollSplitStatus = async (projectId: string): Promise<boolean> => {
    const status = await clausesApi.getSplitStatus(projectId);
    if (status.status === 'completed') {
      await loadClauses(projectId);
      await loadProject(projectId);
      return true;
    }
    if (status.status === 'failed') {
      throw new Error(status.error_message || 'AI 分析失败');
    }
    return false;
  };

  const loadClauses = async (projectId: string) => {
    const response = await clausesApi.list(projectId, { page_size: 200 });
    
    // Convert to Clause type with full content
    const clausesWithContent: Clause[] = await Promise.all(
      response.items.map(async (item) => {
        const detail = await clausesApi.get(projectId, item.id);
        return {
          id: detail.id,
          sequence: detail.sequence,
          clause_no: detail.clause_no,
          title: detail.title,
          content: detail.content,
          party_tags: detail.party_tags,
          source_page: detail.source_page,
          version: detail.version,
          clause_type: detail.clause_type || 'main_body',
          source_span: detail.source_span,
          metadata: detail.metadata,
          created_at: detail.created_at,
        } as Clause;
      })
    );
    
    setState(prev => ({ ...prev, clauses: clausesWithContent.sort(compareClauses) }));
  };

  const startReview = async (projectId: string) => {
    await reviewApi.start(projectId, { use_exemptions: true });
  };

  const pollReviewStatus = async (projectId: string): Promise<{ completed: boolean; progress: number }> => {
    const status = await reviewApi.getStatus(projectId);
    
    if (status.status === 'completed') {
      await loadReviewResults(projectId);
      await loadProject(projectId);
      return { completed: true, progress: 100 };
    }
    
    if (status.status === 'failed') {
      setState(prev => ({ ...prev, error: status.error_message || 'Review failed' }));
      return { completed: true, progress: 0 };
    }
    
    // Load partial results during review
    if (status.completed_count > 0) {
      await loadReviewResults(projectId);
    }
    
    return { completed: false, progress: status.progress };
  };

  const loadReviewResults = async (projectId: string) => {
    const response = await reviewApi.listResults(projectId, { page_size: 200 });
    
    const resultsMap = new Map<string, ReviewResult>();
    for (const item of response.items) {
      try {
        const detail = await reviewApi.getClauseResult(projectId, item.clause_id);
        resultsMap.set(item.clause_id, {
          id: detail.id,
          clause_id: detail.clause_id,
          status: detail.status as ReviewStatus,
          evidence: detail.evidence,
          issues: detail.issues,
          suggested_cn: detail.suggested_cn,
          suggested_en: detail.suggested_en,
          confidence: detail.confidence,
          rationale: detail.rationale,
          uncertain_reasons: detail.uncertain_reasons,
          missing_info: detail.missing_info,
          matched_rules: detail.matched_rules,
          error_message: null,
          retry_count: detail.retry_count,
          created_at: detail.created_at,
          updated_at: detail.updated_at,
        });
      } catch (e) {
        // Skip failed results
      }
    }
    
    setState(prev => ({ ...prev, reviewResults: resultsMap }));
  };

  const reviewSingleClause = async (projectId: string, clauseId: string, force?: boolean): Promise<ReviewResult> => {
    const result = await reviewApi.reviewClause(projectId, clauseId, force);
    
    const reviewResult: ReviewResult = {
      id: result.id,
      clause_id: result.clause_id,
      status: result.status as ReviewStatus,
      evidence: result.evidence,
      issues: result.issues,
      suggested_cn: result.suggested_cn,
      suggested_en: result.suggested_en,
      confidence: result.confidence,
      rationale: result.rationale,
      uncertain_reasons: result.uncertain_reasons,
      missing_info: result.missing_info,
      matched_rules: result.matched_rules,
      error_message: null,
      retry_count: result.retry_count,
      created_at: result.created_at,
      updated_at: result.updated_at,
    };
    
    setState(prev => {
      const newResults = new Map(prev.reviewResults);
      newResults.set(clauseId, reviewResult);
      return { ...prev, reviewResults: newResults };
    });
    
    return reviewResult;
  };

  const saveUserEdit = async (projectId: string, clauseId: string, editLines: string[], finalText?: string, notes?: string) => {
    await userEditsApi.create(projectId, clauseId, {
      edit_lines: editLines,
      final_text: finalText,
      notes,
    });
  };

  const generateReport = async (projectId: string) => {
    await reportsApi.generate(projectId, true);
  };

  const loadReport = async (projectId: string) => {
    const report = await reportsApi.get(projectId);
    setState(prev => ({
      ...prev,
      report: {
        id: report.id,
        project_id: report.project_id,
        version: report.version,
        total_clauses: report.summary.total_clauses,
        compliant_count: report.summary.compliant_count,
        non_compliant_count: report.summary.non_compliant_count,
        uncertain_count: report.summary.uncertain_count,
        ignored_count: report.summary.ignored_count,
        exempted_count: report.summary.exempted_count,
        non_compliant_items: report.non_compliant_items,
        created_at: report.created_at,
      } as Report,
    }));
  };

  const createExport = async (projectId: string, format: 'docx' | 'pdf', options?: any): Promise<ExportJob> => {
    const exportJob = await exportsApi.create(projectId, { format, options });
    setState(prev => ({ ...prev, currentExport: exportJob as ExportJob }));
    return exportJob as ExportJob;
  };

  const pollExportStatus = async (projectId: string, exportId: string): Promise<ExportJob> => {
    const status = await exportsApi.getStatus(projectId, exportId);
    const exportJob: ExportJob = {
      id: status.id,
      project_id: projectId,
      format: 'docx',
      status: status.status,
      progress: status.progress,
      current_stage: status.current_stage,
      download_url: status.download_url,
      version: 1,
      error_message: status.error_message,
      expires_at: status.expires_at,
      created_at: new Date().toISOString(),
    };
    setState(prev => ({ ...prev, currentExport: exportJob }));
    return exportJob;
  };

  const addExemption = async (projectId: string, data: any) => {
    await exemptionsApi.create(projectId, data);
  };

  const loadExemptions = async (projectId: string) => {
    // Load exemptions if needed
    await exemptionsApi.list(projectId);
  };

  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  const setReviewProcess = (update: Partial<ReviewProcessState>) => {
    setState(prev => ({
      ...prev,
      reviewProcess: { ...prev.reviewProcess, ...update },
    }));
  };

  const resetReviewProcess = () => {
    setState(prev => ({
      ...prev,
      reviewProcess: { currentStep: 'idle', stepProgress: 0, errorMessage: null },
    }));
  };

  const value: AppContextType = {
    ...state,
    unifiedAuthEnabled,
    loginWithAzure,
    verifyAzureToken,
    logout,
    checkAuth,
    createProject,
    loadProject,
    updateProject,
    clearCurrentProject,
    uploadFile,
    startSplit,
    loadClauses,
    pollSplitStatus,
    startReview,
    pollReviewStatus,
    loadReviewResults,
    reviewSingleClause,
    saveUserEdit,
    generateReport,
    loadReport,
    createExport,
    pollExportStatus,
    addExemption,
    loadExemptions,
    setReviewProcess,
    resetReviewProcess,
    clearError,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export default AppContext;
